import PropTypes from 'prop-types';
import { useRef, useEffect, useState } from 'react';
import { Autocomplete, TextField, Checkbox, Tooltip, Chip, Box } from '@mui/material';

FilterAutocomplete.propTypes = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default function FilterAutocomplete({ label = "", options = [], value = [], onChange, ...autocompleteProps }) {
  const allOptions = ['All', ...options];

  const chipLabelRef = useRef(null);
  const [chipIsCutoff, setChipIsCutoff] = useState(false);

  useEffect(() => {
    // Wait for DOM update
    const timer = setTimeout(() => {
      if (chipLabelRef.current) {
        setChipIsCutoff(chipLabelRef.current.scrollWidth > chipLabelRef.current.clientWidth);
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [value[0], value.length]);

  const handleChange = (e, newValue) => {
    const hasAllNow = newValue.includes('All');
    const hadAll = value.includes('All');

    if (hasAllNow && !hadAll) {
      onChange([...options, 'All']);
      return;
    }

    if (!hasAllNow && hadAll) {
      onChange([]);
      return;
    }

    const filtered = newValue.filter((opt) => opt !== 'All');

    if (filtered.length === options.length) {
      onChange([...filtered, 'All']);
    } else {
      onChange(filtered);
    }
  };

  const autocompleteHideInputStyle = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    border: 0,
    opacity: 0,
    pointerEvents: 'none',
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={allOptions}
      value={value}
      onChange={handleChange}
      getOptionLabel={(option) => option}
      sx={{
        whiteSpace: 'nowrap',
        '.MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
      renderTags={(selected, getTagProps) => {
        const hiddenCount = selected.length - 1;
        const tagProps = getTagProps({ index: 0 });
        return (
          selected.length > 0 && (
            <Tooltip
              key={selected[0]}
              title={selected?.length > 1 || chipIsCutoff? selected.join(', ') : ''}
              placement="bottom"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: 'white !important',
                    color: 'black !important',
                    boxShadow: '0px 2px 6px rgba(0,0,0,0.15) !important',
                    border: '1px solid #ddd !important',
                    fontSize: '0.8rem !important',
                  },
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  minWidth: 0
                }}>
                <Chip
                  {...tagProps}
                  label={
                    <span
                      ref={chipLabelRef}
                      style={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {selected[0]}
                    </span>
                  }
                  size="small"
                  sx={{
                    fontSize: '0.75rem',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    flex: 1
                  }}
                />
                {hiddenCount > 0 && <Box
                  component="span"
                  sx={{
                    ml: 0.5,
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    userSelect: 'none',
                    flexShrink: 0,
                  }}
                >
                  +{hiddenCount}
                </Box>}
              </Box>
            </Tooltip>
          )
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          size="small"
          fullWidth
          autoComplete="off"
          sx={{
            '& .MuiAutocomplete-input': {
              ... (value.length > 0 ? autocompleteHideInputStyle : {})
            },
          }}
          InputProps={{
            ...params.InputProps,
            sx: {
              display: 'flex',
              flexWrap: 'nowrap',
              overflow: 'hidden',
              alignItems: 'center',
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const checked =
          option === 'All' ? value.includes('All') || value.length === options.length : value.includes(option);
        return (
          <li {...props}>
            <Checkbox checked={checked} size="small" sx={{ mr: 1 }} />
            {option}
          </li>
        );
      }}
      {...autocompleteProps}
    />
  );
}
